import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@base-app';
import { CardGrid, InfoCard } from '@builder';
import { useModal } from '@components/hooks';
import { useFeatureFlags } from '@modules/feature-flags/hooks';
import { FeatureFlags } from '@modules/feature-flags/types';
import { COLLECTION_LOCATION, colors, STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import { SampleCollectionLocation } from '@modules/study-insights/types';
import { ReceivedSamplesProps } from './ReceivedSamples.types';
import {
  StyledBloodDropIcon,
  StyledBriefcaseIcon,
  StyledBuccalIcon,
  StyledHomeIcon,
  StyledReceivedSamplesContainer,
  StyledSalivaIcon,
  StyledTitle,
} from './ReceivedSamples.styles';

import SampleComposition from '../../shared/SampleComposition/SampleComposition';
import SamplePercentageBar from '../../shared/SamplePercentageBar/SamplePercentageBar';
import SamplesViewGraphByCollectionLocationModal from '../../shared/SampleViewGraphByCollectionLocationModal/SampleViewGraphByCollectionLocationModal';
import SampleTitle from '../../shared/SampleTitle/SampleTitle';
import SampleTotal from '../../shared/SampleTotal/SampleTotal';
import ViewOrderIdsModal from '../../shared/ViewOrderIds/ViewOrderIdsModal/ViewOrderIdsModal';

const ReceivedSamples: FC<ReceivedSamplesProps> = ({ data, period, stats }) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const { isFeatureEnabled } = useFeatureFlags();
  const isBuccalEnabled = isFeatureEnabled(FeatureFlags.BUCCAL_SAMPLE_TYPE);
  const { isShowing, toggle: toggleModal } = useModal();
  const { isShowing: isShowingViewOrderIds, toggle: toggleViewOrderIds } = useModal();
  const [collectionLocationModal, setCollectionLocationModal] = useState<SampleCollectionLocation>();

  const isSeriesEmptyInPerson = data.IN_PERSON === undefined;
  const isSeriesEmptyAtHome = data.AT_HOME === undefined;
  const isSeriesEmpty = isSeriesEmptyAtHome || isSeriesEmptyInPerson;
  const totalSamples = !isSeriesEmpty ? data.total : 0;
  const atHomeTotal = !isSeriesEmpty ? data.AT_HOME.total : 0;
  const inPersonTotal = !isSeriesEmpty ? data.IN_PERSON.total : 0;
  const percentageAtPerson = stats.percentChanges?.find(
    (percent) => percent.collectionLocation === COLLECTION_LOCATION.PERSON,
  )?.value;
  const percentageAtHome = stats.percentChanges?.find(
    (percent) => percent.collectionLocation === COLLECTION_LOCATION.HOME,
  )?.value;

  const percentageItems = [
    {
      text: t(COLLECTION_LOCATION.PERSON.toLocaleLowerCase()),
      value: inPersonTotal,
      textColor: colors.darkBlue200,
      bgColor: colors.blue700,
    },
    {
      text: t(COLLECTION_LOCATION.HOME.toLocaleLowerCase()),
      value: atHomeTotal,
      textColor: colors.darkBlue200,
      bgColor: colors.blue800,
    },
  ];

  const handleToggleModal = (collectionLocation: SampleCollectionLocation): void => {
    setCollectionLocationModal(collectionLocation);
    toggleViewOrderIds();
  };

  const handleGraphFormatModal = (): void => {};

  return (
    <Box>
      <Box css={{ marginBottom: '$6' }}>
        <SamplePercentageBar
          label={
            !isSeriesEmpty
              ? t('received_samples.total_samples', { totalSamples })
              : t('received_samples.total_samples_unavailable')
          }
          percentageItems={percentageItems}
          isSeriesEmpty={isSeriesEmpty}
        />
      </Box>
      <Box css={{ marginBottom: '$6' }}>
        <CardGrid
          title={
            <SampleTitle
              icon={<StyledBriefcaseIcon />}
              id="received-samples-in-person"
              label={t('in_person_collection')}
              onClickViewOrderIds={() => handleToggleModal(SampleCollectionLocation.IN_PERSON)}
            />
          }
        >
          <StyledReceivedSamplesContainer>
            <InfoCard css={{ flexBasis: '380px' }} separator="right">
              <SampleTotal
                id="received-samples-in-person-total"
                description={t('received')}
                collectionLocation={SampleCollectionLocation.IN_PERSON}
                onClickButton={handleGraphFormatModal}
                percentage={percentageAtPerson}
                period={period}
                title={t('total_samples_received')}
                totalSamples={data?.IN_PERSON?.total ?? t('data_unavailable')}
              />
            </InfoCard>
            <InfoCard css={{ flexDirection: 'column' }}>
              <StyledTitle data-testid="total-samples-received-title">
                {t('sample_type').toLocaleUpperCase()}
              </StyledTitle>
              <Box css={{ display: 'flex' }}>
                <SampleComposition
                  details={t('of_received_samples')}
                  icon={<StyledBloodDropIcon />}
                  id="received-samples-in-person-blood"
                  isSeriesEmpty={isSeriesEmptyInPerson}
                  percentage={data?.IN_PERSON?.BLOOD?.percentage.toFixed(0)}
                  sampleName={t('blood')}
                  total={data?.IN_PERSON?.BLOOD?.total}
                />
                <SampleComposition
                  details={t('of_received_samples')}
                  icon={<StyledSalivaIcon />}
                  id="received-samples-in-person-saliva"
                  isSeriesEmpty={isSeriesEmptyInPerson}
                  percentage={data?.IN_PERSON?.SALIVA?.percentage.toFixed(0)}
                  sampleName={t('saliva')}
                  total={data?.IN_PERSON?.SALIVA?.total}
                />
                {isBuccalEnabled && (
                  <SampleComposition
                    details={t('of_received_samples')}
                    icon={<StyledBuccalIcon />}
                    id="received-samples-in-person-buccal"
                    isSeriesEmpty={isSeriesEmptyInPerson}
                    percentage={data?.IN_PERSON?.BUCCAL_SWAB?.percentage.toFixed(0)}
                    sampleName={t('buccal')}
                    total={data?.IN_PERSON?.BUCCAL_SWAB?.total}
                  />
                )}
              </Box>
            </InfoCard>
          </StyledReceivedSamplesContainer>
        </CardGrid>
      </Box>
      <Box>
        <CardGrid
          title={
            <SampleTitle
              icon={<StyledHomeIcon />}
              id="received-samples-at-home"
              label={t('at_home_collection')}
              onClickViewOrderIds={() => handleToggleModal(SampleCollectionLocation.AT_HOME)}
            />
          }
        >
          <StyledReceivedSamplesContainer>
            <InfoCard css={{ flexBasis: '380px' }} separator="right">
              <SampleTotal
                id="received-samples-at-home-total"
                collectionLocation={SampleCollectionLocation.AT_HOME}
                description={t('received')}
                onClickButton={handleGraphFormatModal}
                percentage={percentageAtHome}
                period={period}
                title={t('total_samples_received')}
                totalSamples={data?.AT_HOME?.total ?? t('data_unavailable')}
              />
            </InfoCard>
            <InfoCard css={{ flexDirection: 'column' }}>
              <StyledTitle data-testid="total-samples-received-title">
                {' '}
                {t('sample_type').toLocaleUpperCase()}{' '}
              </StyledTitle>
              <Box css={{ display: 'flex' }}>
                <SampleComposition
                  details={t('of_received_samples')}
                  icon={<StyledSalivaIcon />}
                  id="received-samples-at-home-saliva"
                  isSeriesEmpty={isSeriesEmptyAtHome}
                  percentage={data?.AT_HOME?.SALIVA?.percentage.toFixed(0)}
                  sampleName={t('saliva')}
                  total={data?.AT_HOME?.SALIVA?.total}
                />
                {isBuccalEnabled && (
                  <SampleComposition
                    details={t('of_received_samples')}
                    icon={<StyledBuccalIcon />}
                    id="received-samples-at-home-buccal"
                    isSeriesEmpty={isSeriesEmptyAtHome}
                    percentage={data?.AT_HOME?.BUCCAL_SWAB?.percentage.toFixed(0)}
                    sampleName={t('buccal')}
                    total={data?.AT_HOME?.BUCCAL_SWAB?.total}
                  />
                )}
              </Box>
            </InfoCard>
          </StyledReceivedSamplesContainer>
        </CardGrid>
      </Box>
      {isShowing && collectionLocationModal && (
        <SamplesViewGraphByCollectionLocationModal collectionLocation={collectionLocationModal} onClose={toggleModal} />
      )}
      {isShowingViewOrderIds && collectionLocationModal && (
        <ViewOrderIdsModal
          isOpen={isShowingViewOrderIds}
          title={t(`view_order_id.title.received_samples.${collectionLocationModal.toLocaleLowerCase()}`)}
          onClose={() => toggleViewOrderIds()}
          collectionLocation={collectionLocationModal}
        />
      )}
    </Box>
  );
};

export default ReceivedSamples;
